/**
Copyright (C) 2019-2022, Infraclear Inc. All Rights Reserved.
*/

import React, { useEffect, useRef, useState } from "react";
import "../../assets/styles/global.scss";
import "./login.scss";
import { useNavigate } from "react-router-dom";
import InputAdornment from "@mui/material/InputAdornment";
import {
  Box,
  Button,
  IconButton,
  TextField,
  CircularProgress,
  Divider,
  Chip,
} from "@mui/material";
import {
  LoginWithMFAAPI,
  LoginAPI,
  GetUserIDandPassword,
  LoginWithTWOFAAPI,
  SendTwoFASignInCode
} from "../../constants/api-urlConstants";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { ErrorCodes } from "../../constants/messageCodes";
import { MessageText } from "../../constants/messageText";
import { ChangePassword } from "../../components/changePassword/changePasssword";
import instance from "../../utils/axiosInstance";
import { SnackBarMessage } from "../../components/snackBar/snackBar";
import { accessMangerBasePath, docAcqBasePath, docSearchBasePath, docVerificationBasePath, googleAppRedirectUrl, googleAppUrl, refDataBasepath } from "../../constants/env-url";
import { ForgotPassword } from "../../components/forgotPassword/forgotPassword";
import { GenerateBarcode } from "../../components/generateBarcode/generateBarcode";
import { encryptpassword } from "../../constants/commonConstants";
import { whiteSpaceRestrict } from "../../constants/commonConstants";
import { HelpPopup } from "../../components/helpPopup/helpPopup";
import UserExpirePopup from "../../components/userExpirePopup/userExpirePopup";
import CommonPopUp from "../../components/commonPopUp/commonPopUp";
import { handleSaveUserInfo, isDocSearchOnlyUser, redirectToRespectiveAppByRoles, setCookie } from "../../utils";
import { STORAGE } from "../../utils/constant";
import SigninWithGoogleButton from "../../components/signinWithGoogleButton";

export const LoginPage: React.FC = (props) => {
  const navigate = useNavigate();
  const [showCodeBox, setShowCodeBox] = useState<any>(false);
  const [authCodeType, setAuthCodeType] = useState<string>();
  const [showFWDPassword, setShowFWDPassword] = useState<any>(false);
  const [showGenrateBarcode, setShowGenrateBarcode] = useState<any>(false);
  const [errorObject, setErrorObject] = useState<any>({});
  const [showSetNewPassword, setShowSetNewPassword] = useState<any>(false);
  const [showPassword, setShowPassword] = useState<any>(false);
  const [emailValue, setEmail] = useState<any>('');
  const [statusIsLoading, setStatusIsLoading] = React.useState(false);
  const [openUserExiperPopup, setOpenUserExiperPopup] = React.useState(false);
  const [popUpShow, setPopUpShow] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isTwoFASent, setIsTwoFASent] = React.useState(false);
  const [timer, setTimer] = React.useState<number>(30);
  const param = new URLSearchParams(window.location.search);
  const redirectUrl = param.get('redirectUrl');
  const [initial_state, setInitial_state] = useState<any>({
    email: "",
    password: "",
    code: ""
  });
  const [msgConfig, setMsgConfig] = useState({
    type: '',
    message: '',
    snackFlag: false
  })

  const SigninFormValidation = Yup.object().shape({
    email: Yup.string().email(MessageText.VALIDATION_EMAIL_FORMAT).required(MessageText.VALIDATION_EMAIL_REQUIRED),
    show: Yup.boolean(),
    password: Yup.string().required(MessageText.VALIDATION_PASS_REQUIRED_LOGIN),
    code: showCodeBox ?
      ((authCodeType === ErrorCodes.SOFTWARE_TOKEN_TWOFA) ?
        Yup.string().required(MessageText.VALIDATION_TWOFA_CODE_REQUIRED)
        : Yup.string().required(MessageText.VALIDATION_MFA_CODE_REQUIRED)
      )
      : Yup.string().notRequired(),
  });

  useEffect(() => {
    const userCreatedBy = window.location.search;
    if (userCreatedBy && !redirectUrl) {
      getUserIDandPassword(userCreatedBy);
    }
    if (navigator.cookieEnabled) {
      localStorage.removeItem('currentTab')
    }
  }, []);

  const onFinish = (credentials: any) => {
    setStatusIsLoading(true)
    if (credentials) {
      !showCodeBox
        ? authenticateUser(credentials)
        : authenticateMFAUser(credentials);
    } else {
    }
  };

  const authenticateUser = async (credentials: any) => {
    const encrypt_password: any = await encryptpassword(credentials.password);
    const USER_API_BASE_URL = `${LoginAPI}`;
    const userData = {
      email: credentials.email,
      password: encrypt_password
    };
    instance
      .post(USER_API_BASE_URL, userData)
      .then((response: any) => {
        const res = response.data;
        redirectToRespectiveAppByRoles(res?.data?.roles || []);
        setStatusIsLoading(false);
        if (res.success) {
          const isAdmin_value = res?.data?.roles && res?.data?.roles.length > 0 && res?.data?.roles.includes("SUPER_ADMIN");
          const userInfo: any = {
            id: res?.data.id,
            roles: res?.data.roles,
            isAdmin: isAdmin_value
          }
          handleSaveUserInfo(userInfo);
          localStorage.setItem('currentTab', 'applications');
          if (!isDocSearchOnlyUser(res?.data?.roles || [])) {
            window.location.href = '/applications';
          }
        } else {
          setInitial_state({ email: "", password: "", code: "", })
          authenticationCheck(response.data, credentials);
        }
      })
      .catch((error) => {
        setStatusIsLoading(false);
        setSnackbar('error', MessageText.USERNAME_PASS_NO_MATCH);
      });
      setCookie(STORAGE.SSO_USER_DOWNLOADED, '0');
  };

  const authenticationCheck = (response: any, userData: any) => {
    if (response?.message_code === ErrorCodes.TEMP_PASSWORD_EXPIRED) {
      setPopUpShow(true);
    } else if (response?.message_code === ErrorCodes.SOFTWARE_TOKEN_MFA) {
      setSnackbar('info', MessageText.GAUTH_REQUIRED);
      setShowCodeBox(true);
      setAuthCodeType(ErrorCodes.SOFTWARE_TOKEN_MFA);
    } else if (response?.message_code === ErrorCodes.SOFTWARE_TOKEN_TWOFA) {
      const email = formRef?.current?.values?.email || 'your email';
      setSnackbar('info', `${MessageText.TWOFA_REQUIRED} ${email}.`);
      setShowCodeBox(true);
      setAuthCodeType(ErrorCodes.SOFTWARE_TOKEN_TWOFA);
      setIsTwoFASent(true);
      const timerInterval = setInterval(() => {
        setTimer(old => (old - 1));
      }, 1000)
      setTimeout(() => {
        setIsTwoFASent(false);
        clearInterval(timerInterval);
        setTimer(30);
      }, 30000);
    } else if (response?.message_code === ErrorCodes.NEW_PASSWORD_REQUIRED || response?.message_code === ErrorCodes.MFA_SETUP) {
      if (response?.message_code === ErrorCodes.NEW_PASSWORD_REQUIRED) {
        setSnackbar('info', MessageText.FORCE_CHANGE_PASS);
      }
      const errorInfo = {
        error: response.message_code,
        credentials: userData
      }
      setErrorObject(errorInfo);
      setShowSetNewPassword(true);
    } else if (response?.message_code === ErrorCodes.ERROR_INVALID_ATTEMPTS) {
      setSnackbar('error', `${MessageText.USERNAME_PASS_NO_MATCH} ${response.message}`);
    } else if (response?.message_code === ErrorCodes.ERROR_ACCOUNT_DISABLED) {
      setSnackbar('error', MessageText.ERROR_ACCOUNT_DISABLED);
    } else if (response?.message_code === ErrorCodes.PAYG_INVALID_SUBSCRIPTION) {
      setSnackbar('error', MessageText.INACTIVE_TEAM_ACCOUNT_USER_LOGIN);
    } else if (response?.message_code === ErrorCodes.USER_EXPIRED) {      
      setSnackbar('error', MessageText.USER_TRIAL_EXPIRED);
    } else if (response?.message_code === ErrorCodes.PAYG_TRIAL_CREDIT_EXHAUSTED) {
      setSnackbar('error', MessageText.FREE_TRIAL_RENTAL_CREDIT_EXHAUSTED);
    } else if (response?.message_code === ErrorCodes.BULKDATA_API_USER_NOT_AUTHORIZED) {
      setSnackbar('error', MessageText.NOT_AUTHORIZED_USER_SUBSCRIPTION);
    } else {
      setShowSetNewPassword(false);
      setSnackbar('error', MessageText.USERNAME_PASS_NO_MATCH);
    }
  }

  const setSnackbar = (type: 'error' | 'success' | 'warning' | 'info', message: string) => {
    setMsgConfig({
      type: type,
      message: message,
      snackFlag: true
    })
  }

  const authenticateMFAUser = async (credentials: any) => {
    setOpenUserExiperPopup(false);
    const USER_API_BASE_URL = (authCodeType === ErrorCodes.SOFTWARE_TOKEN_TWOFA) ? `${LoginWithTWOFAAPI}` : `${LoginWithMFAAPI}`;
    const encrypt_password: any = await encryptpassword(credentials.password);
    const userData = {
      email: credentials.email,
      password: encrypt_password,
      code: credentials.code,
    };
    instance
      .post(USER_API_BASE_URL, userData)
      .then((response) => {
        const res = response.data;
        setStatusIsLoading(false);
        if (res.success) {
          if (redirectUrl) {
            window.location.href = redirectUrl;
          } else {
            redirectToRespectiveAppByRoles(res?.data?.roles || []);
            const isAdmin_value = res?.data?.roles && res?.data?.roles.length > 0 && res?.data?.roles.includes("SUPER_ADMIN");
            const userInfo: any = {
              id: res?.data.id,
              roles: res?.data.roles,
              isAdmin: isAdmin_value
            }
            handleSaveUserInfo(userInfo);
            if (res.data.modules.length === 1) {
              navigateToRespectiveApp(res.data.modules[0])
            } else if (res.data.modules.length > 1) {
              if (!isDocSearchOnlyUser(res?.data?.roles || [])) {
                window.location.href = '/applications';
              }
            }
          }
        } else {
          if (res.message_code === ErrorCodes.INVALID_MFA_CODE) {
            setSnackbar('error', MessageText.INVALID_MFA_NO_MATCH);
          } else if (res.message_code === ErrorCodes.INVALID_TWOFA_CODE) {
            setSnackbar('error', MessageText.INVALID_TWOFA_NO_MATCH);
          } else if (res.message_code === ErrorCodes.INVALID_USERNAME_PASSWORD) {
            setSnackbar('error', MessageText.INVALID_RESET_CREDS);
          } else if (res.message_code === ErrorCodes.USER_EXPIRED) {
            setOpenUserExiperPopup(true);
            setShowCodeBox(false);
          } else if (res?.message_code === ErrorCodes.ERROR_INVALID_ATTEMPTS) {
            setSnackbar('error', `${MessageText.INVALID_AUTH_CODE} ${res.message}`);
          } else if (res?.message_code === ErrorCodes.ERROR_ACCOUNT_DISABLED) {
            setSnackbar('error', `${MessageText.ERROR_ACCOUNT_DISABLED}`);
          }
        }
        setStatusIsLoading(false);
      })
      .catch((error) => {
        setStatusIsLoading(false);
        setSnackbar('error', MessageText.USERNAME_PASS_NO_MATCH);
      });
  };

  const getUserIDandPassword = (userData: any) => {
    setIsLoading(true);
    const payload: any = {
      input: userData.slice(1)
    };
    const USER_API_BASE_URL = `${GetUserIDandPassword}`;
    instance
      .post(USER_API_BASE_URL, payload)
      .then((response) => {
        const res = response.data;
        setIsLoading(false);
        if (res.success) {
          const credentials = {
            email: res.data.email,
            password: res.data.password
          }
          setInitial_state(
            {
              email: res.data.email,
              password: res.data.password,
              code: "",
            }
          )
          onFinish(credentials);
          setIsLoading(false);
        }
        else {
          if (res.message_code === ErrorCodes.DUPLICATE_USERNAME) {
            setMsgConfig({
              type: 'error',
              message: MessageText.EMAIL_ALREADY_EXIST,
              snackFlag: true
            })
          } else if (res.message_code === ErrorCodes.INVALID_EMAIL) {
            setMsgConfig({
              type: 'error',
              message: MessageText.EMAIL_FORMAT_INVALID,
              snackFlag: true
            })
          } else {
            setMsgConfig({
              type: 'error',
              message: MessageText.WENT_WRONG,
              snackFlag: true
            })
          }
        }
      })
      .catch((error) => {
        setIsLoading(false);
        setMsgConfig({
          type: 'error',
          message: MessageText.WENT_WRONG,
          snackFlag: true
        })
      });
  };

  const redirectTo = (link: any) => {
    window.open(link, "_self");
  }

  const navigateToRespectiveApp = (module: any) => {
    switch (module.key) {
      case "REF_DATA":
        // code block
        redirectTo(`${refDataBasepath}`)
        break;
      case "DOC_VERIFICATION":
        redirectTo(`${docVerificationBasePath}`)
        break;
      case "ACCESS_MANAGER":
        redirectTo(`${accessMangerBasePath}`)
        break;
      case "DOC_SEARCH":
        redirectTo(`${docSearchBasePath}`)
        break;
      case "DOC_ACQUISITION":
        redirectTo(`${docAcqBasePath}`)
        break;
    }
  }

  const goToLogin = (showLogin: any) => {
    if (showLogin.showMsg === true && showLogin?.MFA_EXPIRED) {
      setTimeout(() => {
        setMsgConfig({
          type: 'error',
          message: MessageText.MFA_TOKEN_EXPIRED,
          snackFlag: true
        });
      })
    }
    else if (showLogin.showMsg === true && !showLogin?.MFA_EXPIRED) {
      setTimeout(() => {
        setMsgConfig({
          type: 'success',
          message: MessageText.MFA_TOKEN_ACTIVATED,
          snackFlag: true
        });
      })
    }
    setShowSetNewPassword(showLogin.hideChangepass);
  }

  const showPassChangeMSg = () => {
    setEmail('');
    setShowFWDPassword(false);
    setMsgConfig({
      type: 'info',
      message: MessageText.PASSWORD_CHANGE_SUCC,
      snackFlag: true
    });
  }

  const showBarcodeChangeMSg = (showLogin: any) => {
    setShowGenrateBarcode(false);
    if (showLogin.showMsg) {
      setMsgConfig({
        type: 'info',
        message: MessageText.MFA_TOKEN_ACTIVATED,
        snackFlag: true
      });
    }
  }

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const goToSignIn = () => {
    navigate("/signup");
  }

  const showFWD = () => {
    setShowCodeBox(false);
    setShowFWDPassword(true);
  }

  const showGENCode = () => {
    setShowCodeBox(false);
    setShowGenrateBarcode(true);
  }
  const formRef = useRef<any>();

  const onClickTwoFACode = (email: string = emailValue) => {
    let USER_API_BASE_URL = `${SendTwoFASignInCode}`;

    instance
      .post(USER_API_BASE_URL, { email: email || emailValue })
      .then((response) => {
        if (response.data.success) {
          setIsTwoFASent(true);
          const timerInterval = setInterval(() => {
            setTimer(old => (old - 1));
          }, 1000)
          setTimeout(() => {
            setIsTwoFASent(false);
            clearInterval(timerInterval);
            setTimer(30);
          }, 30000);

          setMsgConfig({
            type: "info",
            message: MessageText.RESET_CODE_SENT,
            snackFlag: true,
          });
        }
      })
      .catch(() => {
        setMsgConfig({
          type: "error",
          message: MessageText.RESET_CODE_FAILED,
          snackFlag: true,
        });
      })
  }

  return (
    <>
      <div className="login-page-class flex-direction-column">
        <div className="pageHeader vertical-align">
          <img
            style={{ width: "220px", paddingLeft: "10px" }} alt=""
            src={require("../../assets/images/infraclearlogo.png")}
          />
          <HelpPopup />
        </div>
        <div className="authWrapper">
          <div className="login-wrapper ">
            <div className="login-popup">
              {!showSetNewPassword && !showFWDPassword && !showGenrateBarcode && !isLoading && (
                <div className="loginWrapper">
                  <span className="title">Sign in</span>
                  <div className="topSpace">
                    <span className="subTitle">New user?</span>
                    <span className="signText" onClick={goToSignIn} >Create Account</span>
                  </div>
                  <Formik
                    initialValues={initial_state}
                    validationSchema={SigninFormValidation}
                    enableReinitialize
                    onSubmit={onFinish}
                    innerRef={formRef}
                  >
                    {({
                      touched,
                      errors,
                      values,
                      setFieldValue,
                      handleChange,
                      handleSubmit,

                    }) => (
                      <Form onSubmit={handleSubmit} >
                        <Box sx={{ mt: 1 }}>
                          <Field
                            as={TextField}
                            margin="normal"
                            error={touched.email && Boolean(errors.email)}
                            fullWidth
                            label="Email*"
                            name="email"
                            size="small"
                            onChange={(event: any) => {
                              setEmail(event.target.value);
                              handleChange(event)
                            }}
                            inputProps={{
                              autoComplete: 'new-password',
                              form: {
                                autoComplete: 'off',
                              },
                            }}
                            InputLabelProps={{ shrink: true }}
                            helperText={<ErrorMessage name="email" />}
                          />
                          <Field
                            as={TextField}
                            margin="normal"
                            fullWidth
                            name="password"
                            label="Password*"
                            type={showPassword ? "text" : "password"}
                            id="password"
                            autoComplete="off"
                            size="small"
                            onChange={handleChange}
                            InputLabelProps={{ shrink: true }}
                            value={values.password}
                            error={touched.password && Boolean(errors.password)}
                            onKeyPress={(e: any) => whiteSpaceRestrict(e)}
                            helperText={<ErrorMessage name="password" />}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end" >
                                  <IconButton tabIndex={-1}
                                    aria-label="toggle password visibility"
                                    onClick={handleClickShowPassword}
                                  >
                                    {showPassword ? (
                                      <Visibility />
                                    ) : (
                                      <VisibilityOff />
                                    )}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                          {showCodeBox &&
                            <div>
                              <Field
                                as={TextField}
                                margin="normal"
                                className="inputField"
                                fullWidth
                                autoComplete="off"
                                name="code"
                                label={`${(authCodeType === ErrorCodes.SOFTWARE_TOKEN_TWOFA) ? "2FA" : "Multi-factor Authentication"} Code*`}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                InputProps={{
                                  endAdornment: (
                                    ErrorCodes.SOFTWARE_TOKEN_TWOFA === authCodeType &&
                                    (<InputAdornment position="end">
                                      {isTwoFASent ?
                                        <span className='timer-count resend-in'>Resend Code in {timer} s</span>
                                        :
                                        <span className='cursor-pointer timer-count resend-code' onClick={() => onClickTwoFACode()}>Resend Code</span>
                                      }
                                    </InputAdornment>)
                                  )
                                }}
                                error={
                                  touched.code && Boolean(errors.code)
                                }
                                helperText={<ErrorMessage name="code" />}
                              />
                            </div>
                          }
                          <div className="loader">
                            {statusIsLoading && <div className="loaderWapper">
                              <CircularProgress
                              />
                            </div>}
                            {
                              !statusIsLoading &&
                              <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                className="buttonWrapper"
                                sx={{ mt: 2, mb: 2 }}
                                style={{ textTransform: "none" }}
                              >
                                Sign in
                              </Button>
                            }
                          </div>
                          <Divider>
                            <Chip label="Or" size="small" />
                          </Divider>
                          <SigninWithGoogleButton />
                          <div className="actionWrapper">
                            <div className="fwdPassword">
                              <span
                                className="forgetPassword" onClick={showFWD}>
                                Forgot password?
                              </span>
                            </div>
                            <div className="fwdPassword">
                              <span className="forgetPassword"
                                onClick={showGENCode}>
                                {MessageText.RESET_MFA}?
                              </span>
                            </div>
                          </div>
                        </Box>
                      </Form>
                    )}
                  </Formik>
                </div>
              )}
              {isLoading && (
                <div className="loginLoaderWrapper">
                  <CircularProgress />
                </div>
              )}
              {openUserExiperPopup &&
                <UserExpirePopup openUserExiperPopup={openUserExiperPopup} closePopup={
                  (value: any) => {
                    if (!value) {
                      formRef.current.resetForm();
                    }
                  }
                } />
              }
              {showSetNewPassword &&
                <div className="flex-direction-column mFAResetWrapper">
                  <ChangePassword goToLoginPopup={goToLogin} errorObjectData={errorObject} />
                </div>
              }
              {showFWDPassword &&
                <div className="flex-direction-column loginWrapper">
                  <ForgotPassword passChangeSuccessfully={showPassChangeMSg}
                    emailIDValue={emailValue}
                    forgotPassClose={() => {
                      setEmail('');
                      setShowFWDPassword(false);
                    }}
                  />
                </div>}
              {showGenrateBarcode &&
                <div className="flex-direction-column mFAResetWrapper">
                  <GenerateBarcode barcodeChangeSuccessfully={showBarcodeChangeMSg}
                    genCodeClose={() => {
                      setShowGenrateBarcode(false);
                    }}
                  />
                </div>}
            </div>
          </div>
        </div>
      </div>
      {
        popUpShow && <CommonPopUp openCommonPopup={popUpShow}
          closePopup={
            (value: any) => {
              if (!value) {
                setPopUpShow(false);
                formRef.current.resetForm();
              }
            }
          }
        />
      }
      {msgConfig.snackFlag && <div>
        <SnackBarMessage snacBarValue={msgConfig}
        />
      </div>}
    </>
  );
};

